import React from "react";
import { AlertTriangle, BellRing, ExternalLink, X } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { departmentDisplayName } from "@/utils/alerts/departmentAlerts.js";

const toDisplayText = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || fallback;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    const text = value
      .map((item) => toDisplayText(item))
      .filter(Boolean)
      .join(", ");

    return text || fallback;
  }

  if (typeof value === "object") {
    return (
      toDisplayText(value.note) ||
      toDisplayText(value.Note) ||
      toDisplayText(value.message) ||
      toDisplayText(value.Message) ||
      toDisplayText(value.title) ||
      toDisplayText(value.Title) ||
      toDisplayText(value.description) ||
      toDisplayText(value.Description) ||
      toDisplayText(value.requestTypeName) ||
      toDisplayText(value.RequestTypeName) ||
      fallback
    );
  }

  return fallback;
};

const firstText = (...values) => {
  for (const value of values) {
    const text = toDisplayText(value);
    if (text) return text;
  }

  return "";
};

const UrgentCallModal = ({ alert, onClose }) => {
  if (!alert) return null;

  const title = firstText(
    alert.title,
    alert.Title,
    alert.message,
    alert.Message,
    "Urgent guest alert"
  );

  const roomNumber = firstText(
    alert.roomNumber,
    alert.RoomNumber,
    alert.roomName,
    alert.RoomName,
    "—"
  );

  const guestName = firstText(
    alert.guestName,
    alert.GuestName,
    alert.guest,
    alert.Guest,
    "Guest"
  );

  const department = firstText(
    alert.department,
    alert.Department,
    alert.targetDepartment,
    alert.TargetDepartment
  );

  const requestType = firstText(
    alert.requestType,
    alert.RequestType,
    alert.requestTypeName,
    alert.RequestTypeName,
    "Request"
  );

  const description = firstText(
    alert.description,
    alert.Description,
    alert.note,
    alert.Note,
    alert.reminder,
    alert.Reminder,
    alert.message,
    alert.Message
  );

  const route =
    typeof alert.route === "string" && alert.route.trim()
      ? alert.route
      : "/hotel/calls";

  return (
    <div className="fixed inset-0 z-[95] flex items-end justify-center bg-slate-950/80 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex max-h-[100dvh] w-full flex-col overflow-hidden rounded-t-[2rem] border border-red-200 bg-white shadow-2xl sm:max-w-md sm:rounded-3xl">
        <div className="bg-red-600 px-5 py-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15">
                <BellRing className="h-5 w-5" />
              </div>

              <div>
                <p className="text-lg font-bold">Urgent guest alert</p>
                <p className="text-sm text-white/85">
                  Immediate attention needed
                </p>
              </div>
            </div>

            <Button
              type="button"
              onClick={onClose}
              className="rounded-xl p-2 text-white/80 hover:bg-white/10 hover:text-white"
              aria-label="Close urgent alert"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
          <div className="rounded-2xl border border-red-100 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-700" />

              <div>
                <p className="font-semibold text-red-900">{title}</p>

                <p className="mt-1 text-sm text-red-800">
                  {roomNumber && roomNumber !== "—"
                    ? `Room ${roomNumber}`
                    : "Room not set"}{" "}
                  · {guestName}
                </p>

                <p className="mt-1 text-sm text-red-700">
                  {departmentDisplayName(department)} · {requestType}
                </p>
              </div>
            </div>
          </div>

          {description ? (
            <p className="text-sm leading-relaxed text-slate-600">
              {description}
            </p>
          ) : null}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="touch-target rounded-xl"
              onClick={onClose}
            >
              Dismiss
            </Button>

            <Button
              asChild
              className="touch-target gap-2 rounded-xl"
              onClick={onClose}
            >
              <Link to={route}>
                Open alert
                <ExternalLink className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UrgentCallModal;